import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, RefreshCw, GripVertical, Play, Wrench, ChevronDown, ChevronRight, AlertTriangle, HardDrive, Pencil, X, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { scheduledJobsApi, adminAuditApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

const IN_FLIGHT = ['running', 'copying', 'exporting', 'cleaning'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Administration > System > Scheduled Jobs
 *
 * Cross-type console for AppCfg_ScheduledJobs (DevTrack 136): what is scheduled, whether it
 * ran, and run-it-now. Grouped by job type, expanding to that type's recent runs — same
 * shape as BackupBrowserPage / AuditTrailAdminPage so the three read alike.
 *
 * Owns TIMING for every job type — enable/disable, frequency, time of day. Those are typed
 * columns every type shares, so editing them here is not type-specific work, and it is how
 * the seeded, disabled AUDIT_PURGE job gets turned on.
 *
 * TYPE-SPECIFIC config stays on the page that understands it: Database Backups owns
 * per-database bacpac retention, the audit pages own their trails' purge. This page links
 * out rather than duplicating forms that would render badly for every type at once.
 *
 * The column worth having: a job whose Job_Type has no registered handler will never run,
 * which otherwise looks exactly like a job that is merely idle. Those are flagged.
 *
 * SysAdmin only (route-guarded in App.js, nav flagged sysAdminOnly).
 * STUI-only: no new classes, no inline styles.
 */
const ScheduledJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [handlers, setHandlers] = useState([]);
  const [runsByType, setRunsByType] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    const onClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await scheduledJobsApi.getJobs();
      setJobs(res.data?.jobs || []);
      setHandlers(res.data?.handlers || []);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load scheduled jobs');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadRuns = useCallback(async (jobType) => {
    try {
      const res = await scheduledJobsApi.getRuns({ jobType, limit: 20 });
      setRunsByType(prev => ({ ...prev, [jobType]: res.data || [] }));
    } catch {
      setRunsByType(prev => ({ ...prev, [jobType]: [] }));
    }
  }, []);

  // A job can take 15 minutes, so poll fast only while something is actually running.
  const busy = !!status?.busy || IN_FLIGHT.includes(status?.run?.status);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await scheduledJobsApi.getStatus();
        if (!cancelled) setStatus(res.data);
      } catch { /* transient — next tick retries */ }
    };
    tick();
    const t = setInterval(tick, busy ? 4000 : 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [busy]);

  // Refresh the list and any open run history when a run finishes, so "last run" is truthful.
  const prevBusy = useRef(false);
  useEffect(() => {
    if (prevBusy.current && !busy) {
      load();
      Object.keys(expanded).filter(k => expanded[k]).forEach(loadRuns);
    }
    prevBusy.current = busy;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  // ── Sweep preview (DevTrack 135) ──────────────────────────────────────────
  // The AUDIT_PURGE job deletes across every history table, and the per-trail modals on the
  // audit pages only cover two of them. Enabling a nightly deleter on a partial picture is
  // the wrong shape, so the full counts live here, next to the switch that turns it on.
  const [sweep, setSweep]           = useState(null);
  const [sweeping, setSweeping]     = useState(false);
  const [showSweep, setShowSweep]   = useState(false);

  const loadSweep = async () => {
    setShowSweep(true);
    setSweeping(true);
    try {
      const res = await adminAuditApi.purgeSweepPreview();
      setSweep(res.data);
    } catch (err) {
      setSweep(null);
      notify.error(err.response?.data?.error || 'Preview failed');
    } finally {
      setSweeping(false);
    }
  };

  // ── Schedule editing (DevTrack 135) ───────────────────────────────────────
  // Timing only. This is how the seeded, disabled AUDIT_PURGE job gets turned on.
  // Type-specific config lives on the page that owns it — see the [→] link per row.
  const [editJob, setEditJob] = useState(null);
  const [saving, setSaving]   = useState(false);

  const openEdit = (j) => setEditJob({
    id: j.id,
    jobType: j.jobType,
    databaseName: j.databaseName,
    isEnabled: j.isEnabled,
    frequency: j.frequency,
    dayOfWeek: j.dayOfWeek,
    runAtUtc: j.runAtUtc
  });

  const saveEdit = async () => {
    if (!/^\d{1,2}:\d{2}$/.test(editJob.runAtUtc || '')) {
      notify.error('Run At must be HH:mm (UTC)');
      return;
    }
    setSaving(true);
    try {
      const wasEnabled = jobs.find(j => j.id === editJob.id)?.isEnabled;
      await scheduledJobsApi.updateSchedule(editJob.id, {
        isEnabled: editJob.isEnabled,
        frequency: editJob.frequency,
        dayOfWeek: editJob.frequency === 'Weekly' ? Number(editJob.dayOfWeek ?? 0) : null,
        runAtUtc: editJob.runAtUtc
      });
      // Enabling a purge job starts unattended deletion, so acknowledge it loudly rather
      // than with the usual footer toast.
      if (!wasEnabled && editJob.isEnabled && editJob.jobType === 'AUDIT_PURGE') {
        notify.warning(`${editJob.jobType} is now ENABLED and will run unattended at ${editJob.runAtUtc}Z.`);
      } else {
        notify.success('Schedule saved');
      }
      setEditJob(null);
      await load();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleType = (jobType) => {
    const open = !expanded[jobType];
    setExpanded(prev => ({ ...prev, [jobType]: open }));
    if (open && !runsByType[jobType]) loadRuns(jobType);
  };

  const handleRunNow = async (job) => {
    try {
      await scheduledJobsApi.runNow(job.id);
      notify.success(`${job.jobType} started for ${job.databaseName} — this takes several minutes`);
      const res = await scheduledJobsApi.getStatus();
      setStatus(res.data);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Could not start the job');
    }
  };

  const handleReconcile = async () => {
    try {
      const res = await scheduledJobsApi.reconcile();
      const { staleRuns, handlerCleanups } = res.data || {};
      if (!staleRuns && !handlerCleanups) notify.success('Nothing to reconcile');
      else notify.warning(`Reconciled: ${staleRuns} stale run(s), ${handlerCleanups} cleanup(s)`);
      await load();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Reconcile failed');
    }
  };

  const statusClass = (s) => {
    if (s === 'succeeded') return 'stui-status-complete';
    if (s === 'failed') return 'stui-status-failed';
    if (IN_FLIGHT.includes(s)) return 'stui-status-active';
    return '';
  };

  const fmtStamp = (iso) => formatTimestamp(iso, { showZone: true }) || '—';

  // runAtUtc is a UTC time of day; today's UTC date anchors it to an instant so the person sees
  // it on their own clock, with the zone.
  const fmtLocalTime = (hhmm) => {
    if (!/^\d{1,2}:\d{2}$/.test(hhmm || '')) return null;
    const [h, m] = hhmm.split(':');
    const day = new Date().toISOString().slice(0, 10);
    return formatTimestamp(`${day}T${h.padStart(2, '0')}:${m}:00Z`, { mode: 'time', showZone: true }) || null;
  };

  const fmtDuration = (sec) => {
    if (sec == null) return '—';
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };

  const scheduleText = (j) => {
    const when = j.frequency === 'Weekly'
      ? `${DAY_NAMES[j.dayOfWeek ?? 0].slice(0, 3)} ${j.runAtUtc}Z`
      : `Daily ${j.runAtUtc}Z`;
    return j.isEnabled ? when : `${when} (disabled)`;
  };

  // Type-specific run detail is raw JSON by design — the console renders it as key/value
  // pairs rather than pretending to know every job type's shape.
  //
  // Nested values need care: a bare `${v}` renders an array of objects as "[object Object]",
  // which is what BACKUP_AUDIT's per-database rows and AUDIT_PURGE's Applied list both looked
  // like. Stay type-agnostic — summarise the shape rather than naming any job type's fields —
  // and hang the full JSON off the cell's title so it is still reachable.
  const detailValue = (v) => {
    if (Array.isArray(v)) {
      if (v.length === 0) return 'none';
      // Arrays of primitives read well inline; arrays of objects only report their count.
      return v.every(x => x === null || typeof x !== 'object') ? v.join(', ') : `${v.length} item(s)`;
    }
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const detailText = (raw) => {
    if (!raw) return '';
    try {
      const o = JSON.parse(raw);
      return Object.entries(o)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => `${k}: ${detailValue(v)}`)
        .join(' · ');
    } catch {
      return raw;
    }
  };

  // Full detail, pretty-printed, for the cell tooltip — so "6 item(s)" is not a dead end.
  const detailFull = (raw) => {
    if (!raw) return '';
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
  };

  const types = [...new Set(jobs.map(j => j.jobType))].sort();
  const enabledCount = jobs.filter(j => j.isEnabled).length;
  const orphanTypes = types.filter(t => !jobs.find(j => j.jobType === t)?.hasHandler);

  if (loading) return <div className="stui-detail-empty"><Clock size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Clock size={20} />
          <h1>Scheduled Jobs</h1>
          <span className="stui-module-header-meta">
            {jobs.length} job{jobs.length === 1 ? '' : 's'} · {enabledCount} enabled · handlers: {handlers.join(', ') || 'none'}
            {busy && status?.run ? ` · ${status.run.status} ${status.run.databaseName}` : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleReconcile(); }}>
                  <Wrench size={16} /><span>Reconcile</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); navigate('/admin/database-backups'); }}>
                  <HardDrive size={16} /><span>Database Backups</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {orphanTypes.length > 0 && (
        <div className="stui-hint">
          <AlertTriangle size={14} /> No handler is registered for: {orphanTypes.join(', ')} —
          jobs of these types will never run.
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="stui-detail-empty">
          <Clock size={48} />
          <p>No scheduled jobs. Create a backup schedule on the Database Backups page.</p>
        </div>
      ) : (
        <div className="stui-datasheet-view">
          <div className="stui-data-grid-container">
            <div className="stui-data-grid-scroll-area">
              <table className="stui-data-grid">
                <thead>
                  <tr>
                    <th className="stui-col-delete"></th>
                    <th>Job / Target</th>
                    <th>Schedule</th>
                    <th>Next Run</th>
                    <th>Last Run</th>
                    <th>Status</th>
                    <th className="stui-inner-table-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {types.map(type => {
                    const typeJobs = jobs.filter(j => j.jobType === type);
                    const isOpen = !!expanded[type];
                    const runs = runsByType[type] || [];
                    const noHandler = !typeJobs[0]?.hasHandler;

                    return (
                      <React.Fragment key={type}>
                        <tr className="stui-clickable" onClick={() => toggleType(type)}>
                          <td className="stui-cell-center stui-td-muted">
                            {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                          </td>
                          <td className="stui-fw-semibold stui-text-nowrap">
                            {type}
                            <span className="stui-td-muted"> · {typeJobs.length} job{typeJobs.length === 1 ? '' : 's'}</span>
                          </td>
                          <td colSpan={3} className="stui-td-muted">
                            {isOpen ? 'Recent runs below' : 'Click to see recent runs'}
                          </td>
                          <td>
                            {noHandler
                              ? <span className="stui-status-failed">no handler</span>
                              : <span className="stui-status-complete">handler ok</span>}
                          </td>
                          <td></td>
                        </tr>

                        {typeJobs.map(j => (
                          <tr key={j.id}>
                            <td></td>
                            <td className="stui-text-nowrap">{j.databaseName}</td>
                            <td className="stui-text-nowrap">{scheduleText(j)}</td>
                            <td className="stui-mono stui-text-nowrap">{fmtStamp(j.nextRunUtc)}</td>
                            <td className="stui-mono stui-text-nowrap">{fmtStamp(j.lastRunUtc)}</td>
                            <td>
                              {j.lastStatus
                                ? <span className={statusClass(j.lastStatus)}>● {j.lastStatus}</span>
                                : <span className="stui-td-muted">never run</span>}
                            </td>
                            <td>
                              <div className="stui-inner-table-cell-actions">
                                {j.jobType === 'AUDIT_PURGE' && (
                                  <button
                                    className="stui-entry-action-btn"
                                    onClick={loadSweep}
                                    title="Preview what this purge would delete, across every table"
                                  >
                                    <Eye size={12} />
                                  </button>
                                )}
                                <button
                                  className="stui-entry-action-btn"
                                  onClick={() => openEdit(j)}
                                  title="Edit when this job runs"
                                >
                                  <Pencil size={12} />
                                </button>
                                <button
                                  className="stui-entry-action-btn"
                                  onClick={() => handleRunNow(j)}
                                  disabled={busy || !j.hasHandler}
                                  title={j.hasHandler ? `Run ${j.jobType} for ${j.databaseName} now` : 'No handler registered'}
                                >
                                  <Play size={12} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {isOpen && (
                          <tr>
                            <td colSpan={7}>
                              {runs.length === 0 ? (
                                <div className="stui-inner-table-empty">No runs recorded yet.</div>
                              ) : (
                                <table className="stui-inner-table">
                                  <thead>
                                    <tr>
                                      <th>Started</th>
                                      <th>Target</th>
                                      <th>Status</th>
                                      <th>Duration</th>
                                      <th>Trigger</th>
                                      <th>By</th>
                                      <th>Detail</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {runs.map(r => (
                                      <React.Fragment key={r.id}>
                                        <tr>
                                          <td className="stui-inner-table-cell-mono stui-text-nowrap">{fmtStamp(r.startedAt)}</td>
                                          <td className="stui-text-nowrap">{r.databaseName}</td>
                                          <td><span className={statusClass(r.status)}>● {r.status}</span></td>
                                          <td className="stui-text-nowrap">{fmtDuration(r.durationSec)}</td>
                                          <td>{r.triggerSource}</td>
                                          <td className="stui-td-muted">{r.runBy || '—'}</td>
                                          <td className="stui-td-muted" title={detailFull(r.detail)}>{detailText(r.detail)}</td>
                                        </tr>
                                        {r.errorMessage && (
                                          <tr>
                                            <td colSpan={7} className="stui-td-muted">{r.errorMessage}</td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showSweep && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3 className="stui-modal-title">Purge Preview — all history tables</h3>
              <button className="stui-modal-close" onClick={() => setShowSweep(false)}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-hint">
                What the AUDIT_PURGE job would delete if it ran right now, using the saved
                policy. Nothing is deleted by looking. Retention is set on
                Administration &rsaquo; Security &rsaquo; Preference Defaults, under
                &ldquo;System — Retention&rdquo;.
              </p>

              {sweeping ? (
                <div className="stui-loading"><RefreshCw size={16} className="stui-spinning" /> Counting…</div>
              ) : !sweep ? (
                <div className="stui-inner-table-empty">No preview available.</div>
              ) : (
                <>
                  <div className="stui-detail-section-header">
                    Would delete {sweep.totalRows} row(s)
                    {sweep.targetsSkippedNever > 0 && (
                      <span className="stui-td-muted">
                        {' '}· {sweep.targetsSkippedNever} target(s) set to never
                      </span>
                    )}
                  </div>
                  <table className="stui-inner-table">
                    <thead>
                      <tr>
                        <th>Target</th>
                        <th>Category</th>
                        <th>Keep (days)</th>
                        <th className="stui-cell-right">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sweep.targets.map((t, i) => (
                        <tr key={`${t.target}-${t.category}-${i}`}>
                          <td className="stui-text-nowrap">{t.target}</td>
                          <td className="stui-td-muted">{t.category}</td>
                          <td className={t.never ? 'stui-td-muted' : ''}>
                            {t.never ? 'never' : t.days}
                          </td>
                          <td className={`stui-cell-right ${t.rows > 0 ? 'stui-fw-semibold' : 'stui-td-muted'}`}>
                            {t.rows}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sweep.totalRows === 0 && (
                    <p className="stui-hint">
                      Nothing is outside its retention window — enabling the job would delete nothing today.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowSweep(false)}>Close</button>
              <button className="stui-btn stui-btn-secondary" onClick={loadSweep} disabled={sweeping}>
                {sweeping ? 'Counting…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editJob && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3 className="stui-modal-title">Schedule — {editJob.jobType}</h3>
              <button className="stui-modal-close" onClick={() => setEditJob(null)}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-hint">
                {editJob.databaseName} — timing only. Retention and other type-specific
                settings live on the page that owns them.
              </p>

              <div className="stui-detail-grid">
                <div className="stui-field-group stui-col-8">
                  <label>Enabled</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editJob.isEnabled ? 'yes' : 'no'}
                    onChange={e => setEditJob({ ...editJob, isEnabled: e.target.value === 'yes' })}
                  >
                    <option value="yes">Enabled</option>
                    <option value="no">Disabled</option>
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Frequency</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editJob.frequency}
                    onChange={e => setEditJob({
                      ...editJob,
                      frequency: e.target.value,
                      dayOfWeek: e.target.value === 'Weekly' ? (editJob.dayOfWeek ?? 0) : null
                    })}
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Day of Week</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editJob.dayOfWeek ?? ''}
                    onChange={e => setEditJob({
                      ...editJob,
                      dayOfWeek: e.target.value === '' ? null : Number(e.target.value)
                    })}
                    disabled={editJob.frequency !== 'Weekly'}
                  >
                    <option value="">—</option>
                    {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Run At (UTC)</label>
                  <input
                    className="stui-field-input"
                    value={editJob.runAtUtc}
                    onChange={e => setEditJob({ ...editJob, runAtUtc: e.target.value })}
                    placeholder="06:00"
                  />
                  {fmtLocalTime(editJob.runAtUtc) && (
                    <span className="stui-hint">{fmtLocalTime(editJob.runAtUtc)}</span>
                  )}
                </div>
              </div>

              {editJob.jobType === 'AUDIT_PURGE' && editJob.isEnabled && (
                <p className="stui-hint">
                  <AlertTriangle size={14} /> This job permanently deletes history rows on its
                  schedule, with no undo. Check the dry-run counts on an audit page first.
                </p>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setEditJob(null)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledJobsPage;
